import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { useTranslation } from "@coworker/locales";
import { useCallback, useMemo, useState } from "react";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import { FixaFormField } from "@coworker/apprestructured/src/shared/wrappers/FixaFormField/FixaFormField";
import styles from "./SelectListItemWithDetails.module.css";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import FixaPill from "@coworker/apprestructured/src/shared/wrappers/FixaPill/FixaPill";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import ErrorAddOn from "../TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";

export type TagItem = {
  value: string;
  label: string;
};

export type ListItemWithDetails = {
  label: string;
  value: string;
  requireDescription?: boolean;
  tags?: TagItem[];
};

type SelectListItemWithDetailsProps = {
  listItems: ListItemWithDetails[];
  value: string;
  label: string;
  description: string;
  selectedTags?: TagItem[];
  maxLength?: number;
  onConfirmClick: (newListItem: {
    value: string;
    label: string;
    description: string;
    tags: TagItem[];
  }) => void;
  disabled: boolean;
  title: string;
  errorMessage?: string;
};

function SelectListItemWithDetails({
  listItems,
  value,
  label,
  description,
  selectedTags = [],
  maxLength,
  onConfirmClick,
  disabled,
  title,
  errorMessage,
}: SelectListItemWithDetailsProps) {
  const [showModal, setShowModal] = useState(false);
  const [newValue, setNewValue] = useState<string>(value);
  const [newLabel, setNewLabel] = useState<string>(label);
  const [newDescription, setNewDescription] = useState<string>(description);
  const [newTags, setNewTags] = useState<TagItem[]>(selectedTags);
  const [shouldValidate, setShouldValidate] = useState(false);

  const { t } = useTranslation();

  const selectedListItem = useMemo(
    () => listItems.find((item) => item.value === newValue),
    [listItems, newValue]
  );

  const requiredDescription = selectedListItem?.requireDescription ?? false;
  const descriptionIsTooLong =
    maxLength !== undefined && newDescription.length > maxLength;
  const validDescription =
    (!requiredDescription || newDescription.trim().length > 0) &&
    !descriptionIsTooLong;

  const descriptionValidationMsg = descriptionIsTooLong
    ? t("maximumCharactersLengthString", { count: maxLength })
    : t("pleaseFillInString");

  const handleOpenModal = useCallback(() => {
    setNewValue(value);
    setNewLabel(label);
    setNewDescription(description);
    setNewTags(selectedTags);
    setShowModal(true);
    setShouldValidate(false);
  }, [value, label, description, selectedTags]);

  const {
    saveGuardedFunction: handleCloseModal,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(
    useCallback(() => {
      setShowModal(false);
      setShouldValidate(false);
    }, [])
  );

  const handleConfirm = useCallback(() => {
    if (!newValue) return;
    if (!validDescription) {
      setShouldValidate(true);
      return;
    }

    onConfirmClick({
      value: newValue,
      label: newLabel,
      description: newDescription,
      tags: newTags,
    });
    setShowModal(false);
    setShouldValidate(false);
    disableSaveGuard();
  }, [
    validDescription,
    onConfirmClick,
    newValue,
    newLabel,
    newDescription,
    newTags,
    disableSaveGuard,
  ]);

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNewDescription(e.target.value);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleItemChange = useCallback(
    (nextValue: string) => {
      setNewValue(nextValue);
      setNewLabel(
        listItems.find((item) => item.value === nextValue)?.label || ""
      );
      setNewDescription("");
      setNewTags([]);
      setShouldValidate(false);
      enableSaveGuard();
    },
    [enableSaveGuard, listItems]
  );

  const handleTagToggle = useCallback(
    (tag: TagItem) => {
      enableSaveGuard();
      setNewTags((prev) => {
        const isSelected = prev.some((prevTag) => prevTag.value === tag.value);
        return isSelected
          ? prev.filter((prevTag) => prevTag.value !== tag.value)
          : [...prev, tag];
      });
    },
    [enableSaveGuard]
  );

  return (
    <>
      <FixaListViewItem
        title={title}
        inset
        onClick={handleOpenModal}
        emphasised={!!label}
        description={label ? t(label) : ""}
        control="navigational"
        controlIcon={null}
        disabled={disabled}
        addOn={errorMessage ? <ErrorAddOn>{errorMessage}</ErrorAddOn> : null}
      />
      <FixaModal
        visible={showModal}
        handleCloseBtn={handleCloseModal}
        data-testid="SelectListItemWithDetails-FixaModal"
      >
        <FixaSheets
          labelledById="task-management-select-item-with-details-header"
          header={
            <FixaModalHeader
              id="task-management-select-item-with-details-header"
              title={title}
              closeBtnClick={handleCloseModal}
            />
          }
          footer={
            <FixaModalFooter renderBorder>
              <FixaButton
                type="primary"
                fluid
                text={t("confirmString")}
                onClick={handleConfirm}
                disabled={!newValue}
              />
            </FixaModalFooter>
          }
          fullSize
        >
          <FixaModalBody>
            <FixaListView id="SelectItemWithDetails-ListView">
              {listItems.map((listItem) => {
                const isSelected = newValue === listItem.value;
                const hasTags = (listItem.tags?.length ?? 0) > 0;
                const requiresValidation =
                  !!listItem.requireDescription || maxLength !== undefined;

                return (
                  <div key={listItem.value}>
                    <FixaListViewItem
                      id={listItem.value}
                      title={t(listItem.label)}
                      onChange={() => handleItemChange(listItem.value)}
                      control="radiobutton"
                      controlProps={{
                        checked: isSelected,
                        value: listItem.value,
                      }}
                      disabled={disabled}
                    />

                    {isSelected && hasTags && (
                      <div className={styles["selectedContent"]}>
                        <FixaFormField
                          valid={validDescription}
                          validation={{
                            type: "error",
                            msg: descriptionValidationMsg,
                            id: "descriptionValidation",
                          }}
                          shouldValidate={shouldValidate && requiresValidation}
                        >
                          <FixaTextArea
                            label={t("description4String")}
                            value={newDescription}
                            onChange={handleDescriptionChange}
                          />
                        </FixaFormField>
                        <FixaText
                          tagName="p"
                          textStyle="Body.S"
                          className={styles["optionalLabel"]}
                        >
                          {t("optionalString")}
                        </FixaText>
                        <div className={styles["pillsContainer"]}>
                          {listItem.tags!.map((tag) => (
                            <FixaPill
                              key={tag.value}
                              label={t(tag.label)}
                              size="small"
                              selected={newTags.some(
                                (prevTag) => prevTag.value === tag.value
                              )}
                              onClick={() => handleTagToggle(tag)}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {isSelected && !hasTags && (
                      <FixaFormField
                        className={styles["formField"]}
                        valid={validDescription}
                        validation={{
                          type: "error",
                          msg: descriptionValidationMsg,
                          id: "descriptionValidation",
                        }}
                        shouldValidate={shouldValidate && requiresValidation}
                      >
                        <FixaTextArea
                          label={t("description4String")}
                          value={newDescription}
                          onChange={handleDescriptionChange}
                        />
                      </FixaFormField>
                    )}
                  </div>
                );
              })}
            </FixaListView>
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
    </>
  );
}

export default SelectListItemWithDetails;
